// RockwallSEOPage — universal template for the Rockwall County SEO cluster.
// Renders a comprehensive, EEAT-compliant local authority page from a config.
// Variants: "hub" (Rockwall County), "city" (per-city), "med" (medication hub).
import { useEffect } from "react";
import { Link } from "react-router-dom";
import {
  CheckCircle2, ShieldCheck, Truck, Lock, Stethoscope,
  FlaskConical, Award, MapPin, ChevronRight, Phone, Calendar,
} from "lucide-react";
import Footer from "@/components/landing/Footer";
import SEO from "@/components/shared/SEO";
import { useLeadModal } from "@/components/shared/LeadProvider";
import {
  CITIES, SHARED_FAQS_LOCAL, TIRZ_VS_SEMA, MEDICAL_REVIEWED,
  ROCKWALL_HUB_PATH, TIRZEPATIDE_HUB_PATH, SEMAGLUTIDE_HUB_PATH,
} from "@/data/rockwallSeo";

const RED  = "#E11D2A";
const NAVY = "#07111F";

function MedicalReviewBadge() {
  return (
    <div className="inline-flex items-start gap-3 px-4 py-3 rounded-md bg-white border border-black/10" data-testid="med-review-badge">
      <Award size={20} style={{ color: RED }} className="shrink-0 mt-0.5" />
      <div>
        <p className="text-[10.5px] tracking-[0.16em] font-extrabold text-black/55 uppercase">Medically Reviewed</p>
        <p className="text-[12.5px] font-extrabold text-black mt-0.5">{MEDICAL_REVIEWED.name}</p>
        <p className="text-[11px] text-black/60 mt-0.5">{MEDICAL_REVIEWED.credentials} · Last reviewed {MEDICAL_REVIEWED.last_reviewed}</p>
      </div>
    </div>
  );
}

function CTAStrip({ headline = "Start Your Assessment", subline = "Provider-reviewed weight loss programs · Shipped to your door · $39 your first week*" }) {
  const { open } = useLeadModal();
  return (
    <section className="relative" style={{ background: NAVY }} data-testid="cta-strip">
      <div className="max-w-6xl mx-auto px-5 md:px-8 py-10 md:py-14 text-center">
        <h3 className="font-heavy text-white leading-tight" style={{ fontSize: "clamp(28px, 4vw, 48px)" }}>
          {headline}
        </h3>
        <p className="text-white/70 mt-3 max-w-2xl mx-auto text-[14px]">{subline}</p>
        <div className="flex flex-wrap justify-center gap-3 mt-6">
          <button
            onClick={() => open({ source: "rockwall-cta" })}
            data-testid="seo-cta-start"
            className="px-7 py-4 font-heavy text-white text-[13px] tracking-[0.14em] uppercase"
            style={{ background: RED, boxShadow: "0 10px 28px rgba(225,29,42,0.4)" }}
          >
            Start Your Assessment →
          </button>
          <button
            onClick={() => open({ source: "rockwall-qualify" })}
            data-testid="seo-cta-qualify"
            className="px-7 py-4 font-heavy text-[13px] tracking-[0.14em] uppercase border-2 border-white text-white hover:bg-white hover:text-black transition-colors"
          >
            See If You Qualify
          </button>
        </div>
      </div>
    </section>
  );
}

function TrustBar() {
  const items = [
    { Icon: Stethoscope, t: "Licensed Providers", s: "Texas-board-certified" },
    { Icon: FlaskConical, t: "Pharmacy Compounded", s: "503A facility" },
    { Icon: Lock, t: "HIPAA Conscious", s: "Encrypted records" },
    { Icon: Truck, t: "Discreet Shipping", s: "Plain packaging" },
    { Icon: ShieldCheck, t: "No Insurance Required", s: "Self-pay, transparent" },
  ];
  return (
    <div className="bg-white border-y border-black/10" data-testid="trust-bar">
      <div className="max-w-6xl mx-auto px-5 md:px-8 py-8 grid grid-cols-2 md:grid-cols-5 gap-5">
        {items.map(({ Icon, t, s }) => (
          <div key={t} className="flex items-start gap-2.5">
            <Icon size={20} style={{ color: RED }} className="shrink-0 mt-0.5" />
            <div>
              <p className="font-extrabold text-[10.5px] tracking-[0.12em] uppercase text-black">{t}</p>
              <p className="text-[10.5px] text-black/60 mt-0.5">{s}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function FAQSection({ faqs }) {
  return (
    <section className="bg-white" data-testid="faq-section">
      <div className="max-w-3xl mx-auto px-5 md:px-8 py-14 md:py-20">
        <p className="text-[11px] font-extrabold tracking-[0.2em] mb-2" style={{ color: RED }}>FREQUENTLY ASKED QUESTIONS</p>
        <h2 className="font-heavy text-black leading-tight mb-8" style={{ fontSize: "clamp(28px, 3.8vw, 44px)" }}>
          Answers from BestRx providers.
        </h2>
        <div className="space-y-4">
          {faqs.map((f, i) => (
            <details key={i} className="group rounded-md border border-black/10 bg-white p-4" data-testid={`faq-${i}`}>
              <summary className="font-extrabold text-black cursor-pointer text-[14px] flex justify-between items-center gap-3">
                <span>{f.q}</span>
                <ChevronRight size={16} className="shrink-0 transition-transform group-open:rotate-90" style={{ color: RED }} />
              </summary>
              <p className="mt-3 text-[13.5px] text-black/75 leading-relaxed">{f.a}</p>
            </details>
          ))}
        </div>
      </div>
    </section>
  );
}

function ClusterLinks({ currentPath }) {
  return (
    <section className="bg-[#F5F6F8] border-y border-black/10" data-testid="cluster-links">
      <div className="max-w-6xl mx-auto px-5 md:px-8 py-10">
        <p className="text-[11px] font-extrabold tracking-[0.2em] mb-2" style={{ color: RED }}>ROCKWALL COUNTY HUB</p>
        <h3 className="font-heavy text-black mb-6" style={{ fontSize: "clamp(22px, 2.8vw, 32px)" }}>Explore your local cluster</h3>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 text-[13px]">
          {currentPath !== ROCKWALL_HUB_PATH && (
            <Link to={ROCKWALL_HUB_PATH} className="px-4 py-3 bg-white border border-black/10 hover:border-[color:var(--rx-red,#E11D2A)] font-extrabold text-black flex items-center justify-between gap-2" data-testid="link-rockwall-county-hub">
              <span>Rockwall County Hub</span><ChevronRight size={14} style={{ color: RED }} />
            </Link>
          )}
          {CITIES.filter((c) => c.path !== currentPath).slice(0, 5).map((c) => (
            <Link key={c.slug} to={c.path} className="px-4 py-3 bg-white border border-black/10 hover:border-[color:var(--rx-red,#E11D2A)] font-extrabold text-black flex items-center justify-between gap-2" data-testid={`link-${c.slug}`}>
              <span>{c.name} Weight Loss</span><ChevronRight size={14} style={{ color: RED }} />
            </Link>
          ))}
          {currentPath !== TIRZEPATIDE_HUB_PATH && (
            <Link to={TIRZEPATIDE_HUB_PATH} className="px-4 py-3 bg-white border border-black/10 hover:border-[color:var(--rx-red,#E11D2A)] font-extrabold text-black flex items-center justify-between gap-2" data-testid="link-tirz-hub">
              <span>Tirzepatide Rockwall County</span><ChevronRight size={14} style={{ color: RED }} />
            </Link>
          )}
          {currentPath !== SEMAGLUTIDE_HUB_PATH && (
            <Link to={SEMAGLUTIDE_HUB_PATH} className="px-4 py-3 bg-white border border-black/10 hover:border-[color:var(--rx-red,#E11D2A)] font-extrabold text-black flex items-center justify-between gap-2" data-testid="link-sema-hub">
              <span>Semaglutide Rockwall County</span><ChevronRight size={14} style={{ color: RED }} />
            </Link>
          )}
        </div>
      </div>
    </section>
  );
}

function Breadcrumbs({ items, langAlt, lang }) {
  // Direct alternate-language link — visible across breadcrumbs row, also
  // strengthens hreflang signal via explicit on-page internal linking.
  const altPath = lang === "es" ? langAlt?.en : langAlt?.es;
  const altLabel = lang === "es" ? "View in English" : "Ver en Español";
  return (
    <nav className="bg-white border-b border-black/5" data-testid="breadcrumbs">
      <div className="max-w-6xl mx-auto px-5 md:px-8 py-3 flex flex-wrap items-center justify-between gap-3">
        <ol className="text-[11.5px] text-black/55 flex items-center gap-1.5 flex-wrap">
          {items.map((it, i) => (
            <li key={i} className="inline-flex items-center gap-1.5">
              {i > 0 && <ChevronRight size={11} className="text-black/30" />}
              {it.to ? <Link to={it.to} className="hover:text-black underline-offset-2 hover:underline">{it.label}</Link> : <span className="text-black font-bold">{it.label}</span>}
            </li>
          ))}
        </ol>
        {altPath && (
          <Link
            to={altPath}
            data-testid="lang-alt-link"
            className="inline-flex items-center gap-1.5 text-[10.5px] font-extrabold tracking-[0.14em] uppercase px-2.5 py-1 border border-black/15 hover:border-[color:var(--rx-red,#E11D2A)] hover:text-[color:var(--rx-red,#E11D2A)] transition-colors"
            hrefLang={lang === "es" ? "en" : "es"}
          >
            {altLabel}
          </Link>
        )}
      </div>
    </nav>
  );
}

function buildLocalSchema({ name, url, description, breadcrumbs, faqs, areaServed }) {
  const fqUrl = (p) => `https://bestrx.online${p}`;
  return {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "MedicalBusiness",
        "@id": fqUrl(url) + "#business",
        name: "BestRx — " + name,
        url: fqUrl(url),
        description,
        priceRange: "$$",
        telephone: "+1-844-703-1780",
        medicalSpecialty: ["Endocrinology", "WeightLoss"],
        areaServed: areaServed.map((a) => ({ "@type": "City", name: a })),
        address: { "@type": "PostalAddress", addressRegion: "TX", addressCountry: "US" },
      },
      {
        "@type": "WebPage",
        "@id": fqUrl(url),
        url: fqUrl(url),
        name,
        description,
        isPartOf: { "@type": "WebSite", url: "https://bestrx.online", name: "BestRx" },
        breadcrumb: { "@id": fqUrl(url) + "#breadcrumbs" },
      },
      {
        "@type": "BreadcrumbList",
        "@id": fqUrl(url) + "#breadcrumbs",
        itemListElement: breadcrumbs.map((b, i) => ({
          "@type": "ListItem",
          position: i + 1,
          item: { "@id": b.to ? fqUrl(b.to) : fqUrl(url), name: b.label },
        })),
      },
      {
        "@type": "FAQPage",
        "@id": fqUrl(url) + "#faq",
        mainEntity: faqs.map((f) => ({
          "@type": "Question", name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      },
      {
        "@type": "Article",
        headline: name,
        author: { "@type": "Organization", name: "BestRx Medical Editorial Board" },
        publisher: { "@type": "Organization", name: "BestRx" },
        datePublished: "2026-06-01",
        dateModified: MEDICAL_REVIEWED.last_reviewed,
        mainEntityOfPage: fqUrl(url),
      },
    ],
  };
}

/* ─────────  THE TEMPLATE  ───────── */
export default function RockwallSEOPage({ config }) {
  const { open } = useLeadModal();
  useEffect(() => { window.scrollTo(0, 0); }, [config.path]);

  const faqs = config.faqs;
  const schema = buildLocalSchema({
    name: config.h1,
    url: config.path,
    description: config.metaDescription,
    breadcrumbs: config.breadcrumbs,
    faqs,
    areaServed: config.areaServed,
  });

  return (
    <main className="bg-white text-black overflow-x-hidden min-h-screen">
      <SEO
        title={config.title}
        path={config.path}
        description={config.metaDescription}
        schema={schema}
        lang={config.lang || "en"}
        langAlt={config.langAlt}
      />
      <Breadcrumbs items={config.breadcrumbs} langAlt={config.langAlt} lang={config.lang || "en"} />

      {/* HERO */}
      <section className="relative" style={{ background: NAVY }}>
        <div className="absolute inset-0 opacity-10 pointer-events-none"
          style={{ backgroundImage: "radial-gradient(circle at 20% 20%, rgba(225,29,42,0.4), transparent 50%), radial-gradient(circle at 80% 80%, rgba(225,29,42,0.25), transparent 60%)" }} />
        <div className="relative max-w-6xl mx-auto px-5 md:px-8 py-14 md:py-20">
          <p className="inline-flex items-center gap-2 text-[11px] font-extrabold tracking-[0.2em] text-white/65 uppercase mb-4">
            <MapPin size={13} style={{ color: RED }} /> {config.eyebrow}
          </p>
          <h1 className="font-heavy text-white leading-[0.95]" style={{ fontSize: "clamp(34px, 5.5vw, 72px)" }} data-testid="seo-h1">
            {config.h1}
          </h1>
          <p className="text-white/75 mt-5 max-w-3xl text-[15px] md:text-[17px] leading-relaxed">{config.heroLead}</p>
          <div className="flex flex-wrap gap-3 mt-7">
            <button
              onClick={() => open({ source: `rockwall:${config.path}` })}
              data-testid="seo-hero-cta-primary"
              className="px-7 py-4 font-heavy text-white text-[13px] tracking-[0.14em] uppercase"
              style={{ background: RED, boxShadow: "0 12px 28px rgba(225,29,42,0.4)" }}
            >
              Start Your Assessment →
            </button>
            <a href="tel:+18447031780" className="px-7 py-4 font-heavy text-[13px] tracking-[0.14em] uppercase border-2 border-white text-white inline-flex items-center gap-2" data-testid="seo-hero-cta-call">
              <Phone size={14} /> 844-703-1780
            </a>
          </div>
          <div className="mt-7"><MedicalReviewBadge /></div>
        </div>
      </section>

      <TrustBar />

      {/* MAIN CONTENT BLOCKS */}
      <article className="max-w-3xl mx-auto px-5 md:px-8 py-14 md:py-20 space-y-12">
        {config.sections.map((s, i) => (
          <section key={i} data-testid={`section-${s.id || i}`}>
            <h2 className="font-heavy text-black leading-tight mb-4" style={{ fontSize: "clamp(24px, 3vw, 36px)" }}>
              {s.h2}
            </h2>
            {s.body.map((p, j) => (
              <p key={j} className="text-[15px] text-black/75 leading-relaxed mb-4">{p}</p>
            ))}
            {s.bullets && (
              <ul className="mt-2 space-y-2">
                {s.bullets.map((b, j) => (
                  <li key={j} className="flex items-start gap-2.5 text-[14px] text-black/80">
                    <CheckCircle2 size={16} style={{ color: RED }} className="shrink-0 mt-0.5" />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
            )}
          </section>
        ))}

        {/* Tirzepatide vs Semaglutide comparison — shown on hub + med pages */}
        {config.showTirzVsSema && (
          <section data-testid="tirz-vs-sema">
            <h2 className="font-heavy text-black leading-tight mb-4" style={{ fontSize: "clamp(24px, 3vw, 36px)" }}>
              Tirzepatide vs Semaglutide — Which Is Right For You?
            </h2>
            <p className="text-[15px] text-black/75 leading-relaxed mb-5">{TIRZ_VS_SEMA.intro}</p>
            <div className="overflow-x-auto border border-black/10 rounded">
              <table className="w-full text-[13px]">
                <thead className="bg-[#F5F6F8]">
                  <tr><th className="px-3 py-2 text-left font-extrabold">Feature</th><th className="px-3 py-2 text-left font-extrabold">Tirzepatide</th><th className="px-3 py-2 text-left font-extrabold">Semaglutide</th></tr>
                </thead>
                <tbody>
                  {TIRZ_VS_SEMA.table.map((r) => (
                    <tr key={r.label} className="border-t border-black/5">
                      <td className="px-3 py-2 font-bold">{r.label}</td>
                      <td className="px-3 py-2 text-black/70">{r.tirz}</td>
                      <td className="px-3 py-2 text-black/70">{r.sema}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        )}
      </article>

      <CTAStrip />
      <FAQSection faqs={faqs} />
      <ClusterLinks currentPath={config.path} />

      {/* Editorial / EEAT footer */}
      <section className="bg-white" data-testid="editorial-eeat">
        <div className="max-w-3xl mx-auto px-5 md:px-8 py-10 border-t border-black/10">
          <p className="text-[11px] font-extrabold tracking-[0.2em] mb-2" style={{ color: RED }}>EDITORIAL STANDARDS</p>
          <p className="text-[13px] text-black/75 leading-relaxed">
            BestRx editorial content is written by clinical staff and reviewed by our Medical Editorial Board —
            a panel of Texas-licensed physicians and clinical pharmacists. We cite peer-reviewed studies, FDA labeling,
            and current GLP-1 pharmacology guidelines. Final medication decisions are made by your assigned licensed
            provider after reviewing your intake. This page does not substitute for individualized medical advice.
          </p>
          <p className="text-[11.5px] text-black/55 mt-3 inline-flex items-center gap-1.5">
            <Calendar size={11} /> Last reviewed {MEDICAL_REVIEWED.last_reviewed}
          </p>
        </div>
      </section>

      <Footer />
    </main>
  );
}
